import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

export interface LearningMaterial {
  id: string;
  topicId: string;
  displayOrder: number;
  prevMaterialId?: string;
  nextMaterialId?: string;
}

export interface Topic {
  id: string;
  name: string;
  description?: string;
  courseId: string;
  displayOrder: number;
  tags: string[];
  estimatedDuration?: number;
  minPercentageCovered?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  learningMaterials?: LearningMaterial[];
  enrollments?: unknown[];
}

export interface CreateTopicDto {
  name: string;
  description?: string;
  courseId: string;
  tags?: string[];
  estimatedDuration?: number;
  minPercentageCovered?: number;
  isActive?: boolean;
  displayOrder?: number;
}

export interface UpdateTopicDto {
  name?: string;
  description?: string;
  courseId?: string;
  tags?: string[];
  estimatedDuration?: number;
  minPercentageCovered?: number;
  isActive?: boolean;
  displayOrder?: number;
}

export interface ReorderMaterialsDto {
  materialIds: string[];
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const topicsApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/topics`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(topicsApiClient);

topicsApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const errorData = error.response?.data as
      | { message?: string; error?: string }
      | undefined;

    const apiError: ApiError = {
      message: errorData?.message || error.message || "An error occurred",
      statusCode: error.response?.status || 0,
      error: errorData?.error,
    };
    return Promise.reject(apiError);
  }
);

// ============================================================================
// TOPICS API FUNCTIONS
// ============================================================================

/**
 * Create a new topic in a course
 */
export async function createTopic(
  topicData: CreateTopicDto
): Promise<ApiResponse<Topic>> {
  const response = await topicsApiClient.post<ApiResponse<Topic>>(
    "",
    topicData
  );
  return response.data;
}

/**
 * Get list of topics with optional course filter
 */
export async function getTopics(
  courseId?: string
): Promise<ApiResponse<Topic[]>> {
  const params = courseId ? { courseId } : {};
  const response = await topicsApiClient.get<ApiResponse<Topic[]>>("", {
    params,
  });
  return response.data;
}

/**
 * Get a single topic by ID
 */
export async function getTopic(topicId: string): Promise<ApiResponse<Topic>> {
  const response = await topicsApiClient.get<ApiResponse<Topic>>(
    `/${encodeURIComponent(topicId)}`
  );
  return response.data;
}

/**
 * Update an existing topic
 */
export async function updateTopic(
  topicId: string,
  updateData: UpdateTopicDto
): Promise<ApiResponse<Topic>> {
  const response = await topicsApiClient.put<ApiResponse<Topic>>(
    `/${encodeURIComponent(topicId)}`,
    updateData
  );
  return response.data;
}

/**
 * Delete a topic by ID
 */
export async function deleteTopic(topicId: string): Promise<ApiResponse> {
  const response = await topicsApiClient.delete<ApiResponse>(
    `/${encodeURIComponent(topicId)}`
  );
  return response.data;
}

/**
 * Reorder learning materials within a topic
 */
export async function reorderTopicMaterials(
  topicId: string,
  reorderData: ReorderMaterialsDto
): Promise<ApiResponse<{ materials: LearningMaterial[] }>> {
  const response = await topicsApiClient.put<
    ApiResponse<{ materials: LearningMaterial[] }>
  >(`/${encodeURIComponent(topicId)}/reorder`, reorderData);
  return response.data;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get all topics for a specific course
 */
export async function getTopicsByCourse(courseId: string): Promise<Topic[]> {
  const response = await getTopics(courseId);
  return response.data || [];
}

/**
 * Check if a topic name is available within a course
 */
export async function isTopicNameAvailable(
  courseId: string,
  topicName: string
): Promise<boolean> {
  try {
    const topics = await getTopicsByCourse(courseId);
    return !topics.some(
      (topic) => topic.name.toLowerCase() === topicName.toLowerCase()
    );
  } catch (error) {
    console.error("Error checking topic name availability:", error);
    return false;
  }
}

/**
 * Toggle the active status of a topic
 */
export async function toggleTopicStatus(
  topicId: string,
  isActive: boolean
): Promise<ApiResponse<Topic>> {
  return updateTopic(topicId, { isActive });
}
